/**
 * Lightweight error reporter — bridge towards Sentry.
 * Sends client errors to the `log-client-error` edge function which writes to `error_logs`.
 *
 * Design goals:
 * - Never throw, never block UI, never log secrets.
 * - Deduplicate identical errors within a short window.
 * - Honor user consent (skipped when analytics consent not granted).
 * - When Sentry is added later, swap the transport here without changing call sites.
 */

const ENDPOINT = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1/log-client-error`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
const RELEASE = (import.meta.env.MODE ?? "development") + "@" + (import.meta.env.VITE_APP_VERSION ?? "dev");

const recent = new Map<string, number>();
const DEDUP_WINDOW_MS = 30_000;
const MAX_QUEUE = 20;
let sent = 0;

type Level = "error" | "warn" | "info";

interface ReportOptions {
  level?: Level;
  context?: Record<string, unknown>;
}

function consentAllowsAnalytics(): boolean {
  try {
    const raw = localStorage.getItem("consent_state");
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed?.accepted_analytics === true;
  } catch {
    return false;
  }
}

function fingerprint(message: string, stack?: string): string {
  const head = (stack ?? "").split("\n").slice(0, 3).join("|");
  return `${message}::${head}`.slice(0, 300);
}

export function reportError(error: unknown, opts: ReportOptions = {}): void {
  if (sent >= MAX_QUEUE) return;
  if (typeof window === "undefined") return;
  if (!consentAllowsAnalytics()) return;

  const err = error instanceof Error ? error : new Error(String(error));
  const fp = fingerprint(err.message, err.stack);
  const now = Date.now();
  const last = recent.get(fp);
  if (last && now - last < DEDUP_WINDOW_MS) return;
  recent.set(fp, now);

  // Primary transport: Sentry (only initialized in production, gated by consent).
  // Best-effort dynamic import so dev/preview builds don't pay for it.
  if (import.meta.env.MODE === "production") {
    import("@sentry/react").then((Sentry) => {
      // Map our Level → Sentry SeverityLevel ("warn" → "warning")
      const sentryLevel = opts.level === "warn" ? "warning" : (opts.level ?? "error");
      Sentry.captureException(err, {
        level: sentryLevel as "error" | "warning" | "info",
        contexts: { app: { ...(opts.context ?? {}) } },
      });
    }).catch(() => { /* swallow */ });
  }

  const payload = {
    message: err.message,
    stack: err.stack,
    url: window.location.href,
    user_agent: navigator.userAgent,
    level: opts.level ?? "error",
    context: opts.context ?? {},
    release: RELEASE,
  };

  sent++;
  // Backup transport: write to error_logs DB (works even if Sentry is down).
  // Fire-and-forget, never blocks UI
  fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: ANON_KEY,
      Authorization: `Bearer ${ANON_KEY}`,
    },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {
    // Swallow: we never want logging to throw
  });
}

let installed = false;
export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    reportError(event.error ?? new Error(event.message), {
      context: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    reportError(event.reason ?? new Error("Unhandled rejection"), {
      context: { type: "unhandledrejection" },
    });
  });
}
